import moment from "moment";
import { printLabel } from "../log/logLabels";

export const ERROR_CODES = {
  PAPER_OUT: "Цаас дууссан",
  PAPER_JAM: "Цаас гацсан",
  TONER_OUT: "Хор дууссан",
  OFFLINE: "Холболт тасарсан",
  // The printer wants a person: on the GX5500 a pulled paper cassette shows up this way
  USER_INTERVENTION: "Принтер хүний оролцоо хүссэн (цаас, таг)",
  UNKNOWN: "Тодорхойгүй",
};

export const errorCodeLabel = (code) => ERROR_CODES[code] || code || "Тодорхойгүй";

// A record without a status predates the refund system and cannot be acted on.
export const STATUSES = {
  pending: {
    label: "Хүлээгдэж буй",
    badge: "text-yellow-700 bg-yellow-100 dark:bg-yellow-700 dark:text-yellow-100",
  },
  refunded: {
    label: "Буцаасан",
    badge: "text-green-700 bg-green-100 dark:bg-green-700 dark:text-green-100",
  },
  rejected: {
    label: "Татгалзсан",
    badge: "text-red-700 bg-red-100 dark:bg-red-700 dark:text-red-100",
  },
  resolved: {
    label: "Хэвлэгдсэн",
    badge: "text-blue-700 bg-blue-100 dark:bg-blue-700 dark:text-blue-100",
  },
  // Failed before the kiosk took payment: a log only, nothing to refund or decide
  no_payment: {
    label: "Төлбөргүй алдаа",
    badge: "text-gray-700 bg-gray-200 dark:bg-gray-600 dark:text-gray-100",
  },
  legacy: {
    label: "Хуучин",
    badge: "text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400",
  },
};

export const statusOf = (item) => STATUSES[item.status] || STATUSES.legacy;

export const money = (value) =>
  value === undefined || value === null
    ? "—"
    : Number(value).toLocaleString() + "₮";

// Kiosks send "No invoice", or nothing, when the error came before any payment
export const invoiceText = (item) => {
  const invoice = (item.invoice || "").trim();
  return invoice && invoice.toLowerCase() !== "no invoice" ? invoice : "—";
};

// printed_pages is null when an older kiosk did not report it
export const pagesText = (item) =>
  (item.printed_pages === null || item.printed_pages === undefined
    ? "тодорхойгүй"
    : item.printed_pages) +
  " / " +
  (item.pages ?? "—");

// What the customer ordered, from the payment (item.order, the cloud's
// utils/printErrorOrder.js): pages of one copy × copies, so 34 pages can be told from 2
// pages in 17 copies, then the sides and the kind. A photo has no sides to name. null
// when the error has no payment behind it.
export const orderText = (item) => {
  const order = item.order;
  if (!order) return null;
  return [
    `${order.pages} хуудас × ${order.copies} хувь`,
    ...(order.photo_type ? [] : [order.side ? "2 тал" : "1 тал"]),
    printLabel(order),
  ].join(" · ");
};

// The file's QR code: the kiosk's own report, or the payment's when the kiosk sent none
export const fileOf = (item) =>
  item.fileid || (item.order && item.order.fileid) || "—";

// Pages that printed correctly as the customer counted them on the refund form. The
// kiosk cannot tell what reached the paper, so this is where the admin starts. null if
// they did not say (an older form, or no form at all).
export const customerPrintedOf = (item) =>
  item.contact && Number.isInteger(item.contact.printed_pages)
    ? item.contact.printed_pages
    : null;

// The refund for the pages the customer says did not print. null if the payment or its
// pages are unknown.
export const refundFromCount = (item, printed) => {
  if (!(item.amount > 0) || !(item.pages > 0) || printed === null) return null;
  const missing = Math.max(0, item.pages - Math.min(printed, item.pages));
  return Math.round((item.amount * missing) / item.pages);
};

// The refund to start from. Without the kiosk's count the backend suggests the whole
// payment, so the customer's own count, when they gave one, comes first. customerPrinted
// is that count, or null when the backend's amount stands.
export const suggestedRefundOf = (item) => {
  const printed = customerPrintedOf(item);
  const kioskCounted = item.printed_pages !== null && item.printed_pages !== undefined;
  const fromCount =
    kioskCounted || !(item.refund_amount > 0) ? null : refundFromCount(item, printed);
  return fromCount === null
    ? { amount: item.refund_amount ?? null, customerPrinted: null }
    : { amount: fromCount, customerPrinted: printed };
};

export const customerCountNote = (item, printed) =>
  "Хэрэглэгч " + printed + "/" + item.pages + " хуудас зөв хэвлэгдсэн гэж мэдэгдсэн.";

// The refund form takes an IBAN (MN and 18 digits); shown in groups of four so it can be
// read out or typed into a bank app. Older contacts hold a plain account number.
const isIban = (value) => /^MN\d{18}$/.test(value || "");

export const accountLabel = (value) => (isIban(value) ? "IBAN" : "Данс");

export const accountText = (value) =>
  isIban(value) ? value.replace(/(.{4})(?=.)/g, "$1 ") : value;

// The number to call: what the customer left through the kiosk's QR comes first, then
// the phone on their account when they were logged in while uploading. null if neither.
export const phoneOf = (item) =>
  (item.contact && item.contact.phone) || (item.user && item.user.phone) || null;

// The backend stores Ulaanbaatar time as if it were UTC, like PaymentLog: show it as is.
export const dateText = (date) =>
  date ? moment.utc(date).format("YYYY-MM-DD HH:mm:ss") : "—";
